// FOUND-11: ResizeObserver + rAF debounce → neko-widget-height.
// Plus: Grow sofort, Shrink mit 400 ms Debounce. Verhindert dass transiente
// Recharts-/Layout-Flicker die iframe-Höhe shrinken und damit Parent-Scroll-Jumps
// auslösen. Permanente Shrinks (z. B. "Neu starten") werden weiter committed,
// nur 400 ms verzögert.

const SHRINK_DEBOUNCE_MS = 400;

export function startHeightSync(
  target: Element,
  send: (height: number) => void,
): () => void {
  let raf = 0;
  let lastSent = 0;
  let pendingShrinkTimer: ReturnType<typeof setTimeout> | null = null;

  const measure = (): number => (target as HTMLElement).scrollHeight ?? 0;

  const commit = (height: number) => {
    lastSent = height;
    send(height);
  };

  const fire = () => {
    if (typeof requestAnimationFrame === "undefined") {
      const height = measure();
      if (height > 0) commit(height);
      return;
    }
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const height = measure();
      if (height <= 0) return;
      if (height >= lastSent) {
        // Wachstum → sofort committen, etwaigen Shrink-Plan abbrechen.
        if (pendingShrinkTimer) {
          clearTimeout(pendingShrinkTimer);
          pendingShrinkTimer = null;
        }
        commit(height);
      } else {
        // Shrink → debounce. Bleibt die Höhe nach SHRINK_DEBOUNCE_MS noch unter
        // lastSent, ist es ein echter Shrink (z. B. Reset); andernfalls hat der
        // Grow-Pfad oben das Timeout bereits abgebrochen.
        if (pendingShrinkTimer) clearTimeout(pendingShrinkTimer);
        pendingShrinkTimer = setTimeout(() => {
          const currentHeight = measure();
          if (currentHeight > 0 && currentHeight < lastSent) {
            commit(currentHeight);
          }
          pendingShrinkTimer = null;
        }, SHRINK_DEBOUNCE_MS);
      }
    });
  };

  const ro = new ResizeObserver(fire);
  ro.observe(target);
  fire();

  return () => {
    ro.disconnect();
    if (typeof cancelAnimationFrame !== "undefined") cancelAnimationFrame(raf);
    if (pendingShrinkTimer) {
      clearTimeout(pendingShrinkTimer);
      pendingShrinkTimer = null;
    }
  };
}
